import React, { useState, useEffect } from "react";
import { Stack, Box, Typography, Card, CardContent, Button, Chip } from "@mui/material";
import { IconPhoto, IconRefresh } from "@tabler/icons-react";
import { PageContainer } from "../components/Container/PageContainer";
import { processThreadMediaWithProxy } from "../utils/mediaDownloader";
import axios from "axios";
import allThreads from "../data/dummy/allThreads.json";
import { getActiveToken } from "../utils/getActiveToken";

const ImageTestPage = () => {
  const [threadData, setThreadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedThread, setProcessedThread] = useState(null);

  const testThreadId = "17975171624717194";
  
  // 🔍 실제 캐러셀 스레드 ID 테스트를 위한 배열
  const testThreadIds = [
    "17975171624717194", // 기존 테스트 ID
    // 추가 테스트 ID들을 여기에 추가할 수 있음
  ];

  const fetchTestThread = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getActiveToken();
      if (!token) {
        throw new Error("Access token not found");
      }

      // 스레드 데이터 가져오기
      const response = await axios.get(
        `https://graph.threads.net/v1.0/${testThreadId}`,
        {
          params: {
            fields: "id,text,timestamp,media_product_type,username,media_type,media_url,permalink,children{media_type,media_url,alt_text},alt_text",
            access_token: token,
          },
        }
      );

      const thread = response.data;
      console.log("🧪 Test thread data:", thread);
      
      // 🔍 전체 API 응답 상세 분석
      console.log("📊 Full API Response Analysis:", {
        threadId: thread.id,
        media_type: thread.media_type,
        hasMediaUrl: !!thread.media_url,
        media_url: thread.media_url,
        childrenRaw: thread.children,
        childrenType: typeof thread.children,
        childrenIsArray: Array.isArray(thread.children),
        childrenKeys: thread.children ? Object.keys(thread.children) : null,
        childrenDataExists: thread.children?.data !== undefined,
        childrenDataIsArray: thread.children?.data ? Array.isArray(thread.children.data) : null,
        allFields: Object.keys(thread)
      });
      
      // 🔍 children 필드 상세 분석
      if (thread.children) {
        console.log("🔍 Children Field Deep Analysis:", {
          value: thread.children,
          type: typeof thread.children,
          isArray: Array.isArray(thread.children),
          hasDataProperty: 'data' in thread.children,
          keys: Object.keys(thread.children),
          stringValue: JSON.stringify(thread.children, null, 2)
        });
      }
      
      // 🔍 캐러셀 미디어 상세 분석
      if (thread.media_type === 'CAROUSEL_ALBUM') {
        // 🔍 children 구조 분석 먼저 수행
        let childrenForAnalysis = [];
        if (thread.children) {
          if (Array.isArray(thread.children)) {
            childrenForAnalysis = thread.children;
          } else if (thread.children.data && Array.isArray(thread.children.data)) {
            childrenForAnalysis = thread.children.data;
          }
        }

        console.log("🎠 CAROUSEL_ALBUM 발견!", {
          id: thread.id,
          hasChildren: childrenForAnalysis.length > 0,
          childrenCount: childrenForAnalysis.length,
          childrenDetails: childrenForAnalysis.map((child, index) => ({
            index: index + 1,
            id: child.id,
            media_type: child.media_type,
            hasMediaUrl: !!child.media_url,
            media_url_preview: child.media_url?.substring(0, 100) + '...',
            alt_text: child.alt_text
          }))
        });
        
        // 🔍 각 child의 전체 필드 확인
        if (childrenForAnalysis.length > 0) {
          childrenForAnalysis.forEach((child, index) => {
            console.log(`📄 Child ${index + 1} full data:`, {
              allFields: Object.keys(child),
              child: child
            });
          });
        }
      } else {
        console.log("🔍 Non-carousel media analysis:", {
          media_type: thread.media_type,
          hasMediaUrl: !!thread.media_url,
          media_url_preview: thread.media_url?.substring(0, 100) + '...'
        });
      }
      
      // 미디어 정보 처리
      const processedThreadData = processThreadWithMedia(thread);
      setThreadData(processedThreadData);

      // 프록시 처리
      if (processedThreadData.hasMedia) {
        const proxiedThread = processThreadMediaWithProxy(processedThreadData);
        setProcessedThread(proxiedThread);
        console.log("🔄 Proxied thread:", proxiedThread);
        
        // 🔍 프록시 처리 후 결과 상세 분석
        console.log("📊 Proxy Processing Result Analysis:", {
          originalMediaUrls: processedThreadData.mediaUrls,
          proxiedMediaUrls: proxiedThread.mediaUrls,
          mediaCount: proxiedThread.mediaUrls?.length || 0,
          isCarousel: proxiedThread.isCarousel,
          usingProxy: proxiedThread.usingProxy,
          mediaProcessed: proxiedThread.mediaProcessed
        });
      } else {
        setProcessedThread(processedThreadData);
      }

      // 🔍 더미 데이터와 비교 분석
      const dummyThread = allThreads.find(t => t.id === testThreadId);
      if (dummyThread) {
        console.log("🆚 API vs Dummy Data Comparison:", {
          testThreadId,
          api: {
            media_type: thread.media_type,
            hasMediaUrl: !!thread.media_url,
            hasChildren: !!(thread.children && thread.children.length > 0),
            childrenCount: thread.children?.length || 0
          },
          dummy: {
            media_type: dummyThread.media_type,
            hasMediaUrl: !!dummyThread.media_url,
            hasChildren: !!(dummyThread.children && dummyThread.children.length > 0),
            childrenCount: dummyThread.children?.length || 0
          },
          comparison: {
            mediaTypeMatch: thread.media_type === dummyThread.media_type,
            childrenCountMatch: (thread.children?.length || 0) === (dummyThread.children?.length || 0)
          }
        });
      } else {
        console.log("❌ Dummy thread not found for ID:", testThreadId);
      }

    } catch (err) {
      console.error("❌ Error fetching test thread:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 미디어 정보를 처리하는 함수 (CollectionPage.js와 동일)
  const processThreadWithMedia = (thread) => {
    console.log('🔍 Processing thread for media:', {
      id: thread.id,
      hasMediaUrl: !!thread.media_url,
      media_url: thread.media_url,
      media_type: thread.media_type,
      hasChildren: !!(thread.children && thread.children.length > 0),
      children: thread.children,
      alt_text: thread.alt_text
    });

    // 🔍 children 구조 분석 - Threads API는 {data: []} 형태
    let childrenArray = [];
    if (thread.children && thread.children.data && Array.isArray(thread.children.data)) {
      childrenArray = thread.children.data;
      console.log('📸 Children found in data array:', childrenArray.length);
    } else if (thread.children) {
      console.log('⚠️ Unexpected children structure:', thread.children);
    }

    const hasMedia = !!(thread.media_url || childrenArray.length > 0);
    const mediaUrls = [];
    
    if (childrenArray.length > 0) {
      // 캐러셀/다중 미디어
      console.log('📸 Processing carousel media:', childrenArray);
      childrenArray.forEach((child, index) => {
        console.log(`📸 Processing child ${index + 1}:`, child);
        if (child.media_url) {
          mediaUrls.push({
            url: child.media_url,
            type: child.media_type,
            alt: child.alt_text || ''
          });
        }
      });
    } else if (thread.media_url) {
      // 단일 미디어
      console.log('📸 Processing single media:', thread.media_url);
      mediaUrls.push({
        url: thread.media_url,
        type: thread.media_type,
        alt: thread.alt_text || ''
      });
    }

    return {
      ...thread,
      hasMedia,
      mediaType: thread.media_type,
      mediaUrls,
      isCarousel: !!(childrenArray.length > 1)
    };
  };

  useEffect(() => {
    fetchTestThread();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <Typography variant="h4" gutterBottom>
          이미지 프록시 테스트 - 로딩 중...
        </Typography>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Typography variant="h4" gutterBottom>
          이미지 프록시 테스트 - 에러 발생
        </Typography>
        <Typography color="error">{error}</Typography>
        <Button 
          variant="contained" 
          onClick={fetchTestThread}
          startIcon={<IconRefresh />}
          sx={{ mt: 2 }}
        >
          다시 시도
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Typography variant="h4" gutterBottom>
          이미지 프록시 테스트
        </Typography>
        
        <Typography variant="h6">
          테스트 스레드 ID: {testThreadId}
        </Typography>

        {threadData && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                원본 스레드 데이터
              </Typography>
              <Typography variant="body2" paragraph>
                {threadData.text}
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  icon={<IconPhoto />}
                  label={`미디어: ${threadData.hasMedia ? 'YES' : 'NO'}`}
                  color={threadData.hasMedia ? 'success' : 'default'}
                />
                {threadData.hasMedia && (
                  <Chip
                    label={`${threadData.mediaUrls.length}개 미디어`}
                    variant="outlined"
                  />
                )}
                {threadData.isCarousel && (
                  <Chip
                    label="캐러셀"
                    color="info"
                  />
                )}
              </Stack>

              {threadData.hasMedia && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    원본 미디어 URLs:
                  </Typography>
                  {threadData.mediaUrls.map((media, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        <strong>URL:</strong> {media.url}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {media.type}
                      </Typography>
                      
                      {/* 원본 이미지 (CORS 에러 예상) */}
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          원본 이미지 (CORS 에러 예상):
                        </Typography>
                        <img
                          src={media.url}
                          alt={media.alt || `원본 미디어 ${index + 1}`}
                          style={{
                            width: '200px',
                            height: '200px',
                            objectFit: 'cover',
                            border: '1px solid #ccc'
                          }}
                          onLoad={() => console.log(`✅ 원본 이미지 ${index + 1} 로드 성공`)}
                          onError={(e) => {
                            console.log(`❌ 원본 이미지 ${index + 1} 로드 실패`);
                            e.target.style.backgroundColor = '#ffebee';
                            e.target.alt = '원본 이미지 로드 실패';
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {processedThread && processedThread.hasMedia && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                프록시 처리된 스레드 데이터
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label={processedThread.usingProxy ? '프록시 사용' : '원본 사용'}
                  color={processedThread.usingProxy ? 'success' : 'warning'}
                />
                <Chip
                  label={`처리됨: ${processedThread.mediaProcessed ? 'YES' : 'NO'}`}
                  color={processedThread.mediaProcessed ? 'success' : 'default'}
                />
              </Stack>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  프록시된 미디어 URLs:
                </Typography>
                {processedThread.mediaUrls.map((media, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                      <strong>프록시 URL:</strong> {media.url}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                      <strong>원본 URL:</strong> {media.originalUrl}
                    </Typography>
                    <Typography variant="body2">
                      <strong>프록시 사용:</strong> {media.isProxied ? 'YES' : 'NO'}
                    </Typography>
                    
                    {/* 프록시된 이미지 */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        프록시된 이미지:
                      </Typography>
                      <img
                        src={media.url}
                        alt={media.alt || `프록시 미디어 ${index + 1}`}
                        style={{
                          width: '200px',
                          height: '200px',
                          objectFit: 'cover',
                          border: '1px solid #ccc'
                        }}
                        onLoad={() => console.log(`✅ 프록시 이미지 ${index + 1} 로드 성공`)}
                        onError={(e) => {
                          console.log(`❌ 프록시 이미지 ${index + 1} 로드 실패`);
                          e.target.style.backgroundColor = '#ffebee';
                          e.target.alt = '프록시 이미지 로드 실패';
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        <Button 
          variant="contained" 
          onClick={fetchTestThread}
          startIcon={<IconRefresh />}
        >
          테스트 다시 실행
        </Button>
      </Stack>
    </PageContainer>
  );
};

export default ImageTestPage;